import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Image, Text } from 'shared/Typhography';
import { FlexContainer } from 'shared/Typhography/flex-box';
import { Controller, FormProvider, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useForm, useLiveData } from 'hooks';
import {
  StoryChapterFields,
  StoryChapterFieldsType,
  StoryFields,
  StoryFieldsType,
  storyShema,
} from 'validations/story';
import { Button, Input, SelectForm } from 'shared/Components';
import { theme } from 'theme';
import { Timestamp, doc, runTransaction } from 'firebase/firestore';
import { firestore } from 'services';
import { GOTIVA_UID, ONLY_APPROVED_ARTISTS_QUERY } from 'app-constants';
import { ObjectValues, STORY_STATUSES } from 'types';
import { LIMITED_EDITING_STATUSES } from 'modules/dashboard/pages/ManageStoryScreen/constants/limitedEditingStatuses';

import DaterangePicker from '../GameEvents/components/GameEventForm/components/DaterangePicker';

import { FileState } from './types';
import { HasAudio, HasImageOrVideo, NoFile, TextEditor } from './components';
import { StoryChapterForm } from './segments';
import { useStory } from './hooks';
import { prepareChaptersForUpdate, prepareStoryForUpdate } from './utilities';
import { FileUpload } from './components/FileUpload';

export const ManageStoryScreen = () => {
  const { id: paramId } = useParams<{ id?: string }>();
  const history = useHistory();
  const isCreate = useMemo(() => !paramId, [paramId]);
  const {
    story: existingStoryData,
    chapters: existingChaptersData,
    isLoading: isLoadingStoryData,
  } = useStory(paramId);

  const [isReady, setReady] = useState<boolean>(!paramId ? true : false);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const { docs: artistsData, state: artistsFetchState } = useLiveData(ONLY_APPROVED_ARTISTS_QUERY);

  const chaptersToRemoveRef = useRef<string[]>([]);

  const isFullyEditable = useMemo(
    () => Boolean(!existingStoryData?.exists() || existingStoryData?.data()?.status === 'draft'),
    [existingStoryData],
  );

  const {
    formState: { errors },
    control,
    formState,
    trigger,
    setValue,
    handleSubmit,
    watch,
    reset,
    ...methods
  } = useForm<StoryFieldsType>({
    schema: storyShema,
  });

  const {
    fields: chapters,
    append,
    update,
    remove,
    swap,
  } = useFieldArray({
    name: StoryFields.Chapters,
    control,
  });

  const filesRef = useRef<{ [key: string]: File }>({});

  const submit = useCallback(
    (publish: boolean) =>
      handleSubmit(async (formData) => {
        setSubmitting(true);
        try {
          const storyData = await prepareStoryForUpdate(
            formData,
            publish,
            filesRef.current,
            paramId,
          );

          if (storyData.errors?.length && storyData.errors?.length > 0) {
            toast.error(storyData.errors.join('\n'));
            setSubmitting(false);
            return;
          }

          if (!storyData?.docRef) {
            setSubmitting(false);
            return;
          }

          const chaptersData = await prepareChaptersForUpdate(
            storyData.docRef.id,
            formData.chapters,
            filesRef.current,
          );

          if (chaptersData.errors?.length && chaptersData.errors?.length > 0) {
            toast.error(chaptersData.errors.join('\n'));
            setSubmitting(false);
            return;
          }
          if (!chaptersData.preparedChapters) {
            setSubmitting(false);
            return;
          }

          const chaptersToRemove = chaptersToRemoveRef.current.map((chapterId) =>
            doc(firestore, `stories/${storyData.docRef.id}/chapters/${chapterId}`),
          );

          await runTransaction(firestore, async (tx) => {
            await Promise.all([
              tx.set(storyData.docRef, storyData.data, { merge: true }),
              ...chaptersData.preparedChapters.map((chapterData) =>
                tx.set(chapterData.docRef, chapterData.data, { merge: true }),
              ),
              ...chaptersToRemove.map((chapterToRemoveRef) => tx.delete(chapterToRemoveRef)),
            ]);
          });

          toast.success(isCreate ? 'Story is created' : 'Story is updated');
        } catch (err) {
          toast.error(`Failed to preform all update actions! ${err}`);
          console.log(err);
        }
        setSubmitting(false);

        if (isCreate) {
          reset();

          history.go(0);
        } else {
          history.goBack();
        }
      }),
    [paramId, isCreate],
  );

  const addChapter = useCallback(
    (type: 'static' | 'video') => {
      append({
        [StoryChapterFields.ChapterNumber]: chapters.length + 1,
        [StoryChapterFields.Type]: type,
        [StoryChapterFields.Id]: `virtual-${Math.random() * 100000000}`.replaceAll('.', '-'),
        [StoryChapterFields.RenderKey]: `virtual/${Math.random() * 100000000}`,
      });
    },
    [append, chapters],
  );

  const updateChapter = useCallback(
    (index: number, data: Partial<StoryChapterFieldsType>) => {
      update(index, {
        ...chapters[index],
        ...data,
        [StoryChapterFields.RenderKey]: `virtual/${Math.random() * 100000000}`,
      });
    },
    [chapters],
  );

  const removeChapter = useCallback(
    (index: number) => {
      if (!isCreate && !chapters[index][StoryChapterFields.Id].includes('virtual')) {
        chaptersToRemoveRef.current.push(chapters[index][StoryChapterFields.Id]);
      }

      remove(index);
    },
    [chapters, isCreate],
  );

  const creatorList = useMemo(
    () =>
      artistsData?.map((creator) => ({
        value: creator?.id,
        label: (
          <FlexContainer gap="1rem" alignItems="center" flexDirection="row">
            <Image
              size="profileImage"
              borderRadiusType="circle"
              src={creator?.data()?.profileImageUrl}
            />
            {creator?.data()?.Name} {creator?.data()?.username}
          </FlexContainer>
        ),
        searchValue: creator.data().Name,
      })),
    [artistsData],
  );

  const gotivaCreator = useMemo(
    () => creatorList?.find((creator) => creator.value === GOTIVA_UID),
    [creatorList],
  );

  const setInitialData = useCallback(() => {
    if (
      existingChaptersData &&
      existingStoryData &&
      creatorList &&
      artistsFetchState === 'success'
    ) {
      setReady(false);

      const storyData = existingStoryData?.data();
      setValue(StoryFields.AvilableDateRange, {
        start: storyData?.publishDate ? (storyData?.publishDate as Timestamp).toDate() : undefined,
        end: storyData?.expirationDate
          ? (storyData?.expirationDate as Timestamp).toDate()
          : undefined,
      });

      if (storyData?.entryFee?.coins) {
        setValue(StoryFields.EnteryFee, storyData?.entryFee?.coins);
      }

      if (storyData?.reward?.coins) {
        setValue(StoryFields.StoryReward, storyData?.reward?.coins);
      }

      if (storyData?.imageUrl) {
        setValue(StoryFields.ImageUrl, { state: 'exists', url: storyData?.imageUrl });
      }

      if (storyData?.titleImageUrl) {
        setValue(StoryFields.TitleImageUrl, { state: 'exists', url: storyData?.titleImageUrl });
      }

      if (storyData?.soundtrackUrl) {
        setValue(StoryFields.SoundtrackUrl, { state: 'exists', url: storyData?.soundtrackUrl });
      }

      if (storyData?.storyAbout) {
        setValue(StoryFields.StoryAbout, storyData?.storyAbout);
      }

      if (storyData?.storyEnd) {
        setValue(StoryFields.StoryEnd, storyData?.storyEnd);
      }

      if (storyData?.title) {
        setValue(StoryFields.Title, storyData?.title);
      }

      if (storyData?.publisher) {
        const publisher = creatorList.find((creator) => creator?.value === storyData?.publisher);
        if (publisher) {
          setValue(StoryFields.Publisher, publisher);
        }
      }

      const mappedChapters = existingChaptersData?.docs?.map((existingChapter) => ({
        [StoryChapterFields.Id]: existingChapter.id,
        [StoryChapterFields.ChapterIntroduction]: existingChapter.data()?.chapterIntroduction,
        [StoryChapterFields.ChapterNumber]: existingChapter?.data()?.chapterNumber,
        [StoryChapterFields.Difficulty]: {
          value: existingChapter?.data()?.difficulty,
          // To prevent any string entering into label
          // Right now we only have square type of puzzle, if this changes
          // 'replace(/\D/g, "")' part has to be removed
          label: existingChapter?.data()?.difficulty.replace(/\D/g, ''),
        },
        [StoryChapterFields.Type]: existingChapter?.data()?.type,
        [StoryChapterFields.Original]: {
          state: 'exists' as FileState['state'],
          url: existingChapter?.data()?.originalUrl ?? '',
        },
        [StoryChapterFields.Video]: {
          state: 'exists' as FileState['state'],
          url: existingChapter?.data()?.url ?? '',
        },
        [StoryChapterFields.VideoIconUrl]: {
          state: 'exists' as FileState['state'],
          url: existingChapter?.data()?.videoIconUrl ?? '',
        },
        [StoryChapterFields.PreviewPlaceholderUrl]: {
          state: 'exists' as FileState['state'],
          url: existingChapter?.data()?.staticPreviewPlaceholderUrl ?? '',
        },
        [StoryChapterFields.VideoPreviewUrl]: {
          state: 'exists' as FileState['state'],
          url: existingChapter?.data()?.videoPreviewUrl ?? '',
        },
        [StoryChapterFields.IconUrl]: {
          state: 'exists' as FileState['state'],
          url: existingChapter?.data()?.iconUrl ?? '',
        },
        [StoryChapterFields.RenderKey]: `virtual/${Math.random() * 100000000}`,
      }));

      const sortedChapters = mappedChapters?.sort(
        (mappedChapter, mappedChapterNext) =>
          mappedChapter[StoryChapterFields.ChapterNumber] -
          mappedChapterNext[StoryChapterFields.ChapterNumber],
      );

      // eslint-disable-next-line
      // @ts-ignore
      setValue(StoryFields.Chapters, sortedChapters);
      setReady(true);
    }
  }, [existingChaptersData, existingStoryData, creatorList, artistsFetchState]);

  useEffect(() => {
    setInitialData();
  }, [paramId, existingChaptersData, existingStoryData, artistsFetchState, creatorList]);

  const isReadyToRender = useMemo(
    () => !isLoadingStoryData && isReady && gotivaCreator !== undefined,
    [isLoadingStoryData, isReady, gotivaCreator],
  );

  return (
    <FlexContainer
      margin={{ marginTop: '150px' }}
      dimensions={{ width: '100%' }}
      justifyContent="center"
    >
      <Box width="80%">
        <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="20px">
          <Text size={2} weight="bold">
            {isCreate ? <>Create new </> : <>Update </>} Story
          </Text>
          {isReadyToRender && (
            <FormProvider
              control={control}
              formState={formState}
              setValue={setValue}
              trigger={trigger}
              handleSubmit={handleSubmit}
              watch={watch}
              reset={reset}
              {...methods}
            >
              <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="50px">
                <FlexContainer
                  dimensions={{ width: '100%', minHeight: '256px' }}
                  flexDirection="column"
                  gap="10px"
                >
                  <Text weight="bold" size={1.5}>
                    Story cover
                  </Text>
                  <FileUpload
                    isEditDisabled={!isFullyEditable}
                    initialState={
                      existingStoryData?.data()?.imageUrl
                        ? { state: 'exists', url: existingStoryData?.data()?.imageUrl }
                        : undefined
                    }
                    onFileAddedHook={(fileState, file) => {
                      setValue(StoryFields.ImageUrl, fileState);
                      if (file) {
                        filesRef.current.image = file;
                      }
                    }}
                    hasFileRender={(
                      fileState?: FileState,
                      removeFile?: () => void,
                      disabled?: boolean,
                    ) => (
                      <HasImageOrVideo
                        fileState={fileState}
                        removeImage={removeFile}
                        disabled={disabled}
                      />
                    )}
                    noFileRender={() => <NoFile fileTypes={['png', 'jpeg', 'jpg']} />}
                    fileUploadProps={{
                      styling: {
                        dimensions: { flex: '1', width: '100%', minHeight: '300px' },
                        gap: '10px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { padding: '16px' },
                      },
                      accept: 'image/jpg,image/jpeg,image/png',
                    }}
                  />
                </FlexContainer>

                <FlexContainer
                  dimensions={{ width: '100%', minHeight: '256px' }}
                  flexDirection="column"
                  gap="10px"
                >
                  <Text weight="bold" size={1.5}>
                    Story title image
                  </Text>
                  <FileUpload
                    isEditDisabled={!isFullyEditable}
                    initialState={
                      existingStoryData?.data()?.titleImageUrl
                        ? { state: 'exists', url: existingStoryData?.data()?.titleImageUrl }
                        : undefined
                    }
                    onFileAddedHook={(fileState, file) => {
                      setValue(StoryFields.TitleImageUrl, fileState);
                      if (file) {
                        filesRef.current.titleImage = file;
                      }
                    }}
                    hasFileRender={(
                      fileState?: FileState,
                      removeFile?: () => void,
                      disabled?: boolean,
                    ) => (
                      <HasImageOrVideo
                        fileState={fileState}
                        removeImage={removeFile}
                        disabled={disabled}
                      />
                    )}
                    noFileRender={() => <NoFile fileTypes={['png']} />}
                    fileUploadProps={{
                      styling: {
                        dimensions: { flex: '1', width: '100%', minHeight: '300px' },
                        gap: '10px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { padding: '16px' },
                      },
                      accept: 'image/png',
                    }}
                  />
                </FlexContainer>

                <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="10px">
                  <FlexContainer
                    flexDirection="row"
                    flexWrap="wrap"
                    dimensions={{ width: '100%' }}
                    gap="10px"
                  >
                    <FlexContainer
                      flexDirection="column"
                      alignContent="center"
                      justifyContent="center"
                      dimensions={{ flex: 1 }}
                      gap="50px"
                    >
                      <Input
                        label="Story name"
                        required
                        control={control}
                        type="text"
                        width="100%"
                        outerWidth="100%"
                        name={StoryFields.Title}
                        error={errors[StoryFields.Title]}
                        minWidth="200px"
                        placeholder={`Enter ${StoryFields.Title}`}
                        disabled={!isFullyEditable}
                      />
                      <Input
                        label="Entery fee"
                        required
                        control={control}
                        type="number"
                        width="100%"
                        outerWidth="100%"
                        name={StoryFields.EnteryFee}
                        error={errors[StoryFields.EnteryFee]}
                        minWidth="200px"
                        placeholder="Story entery fee (0 - free)"
                        disabled={!isFullyEditable}
                        defaultValue={0}
                      />
                    </FlexContainer>
                    <FlexContainer
                      flexDirection="column"
                      justifyContent="center"
                      dimensions={{ flex: 1, maxWidth: '100%' }}
                      gap="50px"
                    >
                      <Controller
                        name={StoryFields.AvilableDateRange}
                        control={control}
                        render={({ field }) => (
                          <FlexContainer
                            flexDirection="column"
                            gap="10px"
                            dimensions={{ width: '100%' }}
                          >
                            <FlexContainer
                              flexDirection="row"
                              justifyContent="center"
                              gap="20px"
                              alignItems="center"
                              dimensions={{ width: '100%' }}
                            >
                              <DaterangePicker
                                isClearable={true}
                                labelStartDate="Publish at"
                                startDate={field?.value?.start ?? null}
                                endDate={null}
                                selectsRange={false}
                                inline={true}
                                alwaysAsArray={true}
                                disabled={!isFullyEditable}
                                onChange={(range) => {
                                  range[0] &&
                                    setValue(StoryFields.AvilableDateRange, {
                                      start: range[0],
                                      end: field?.value?.end,
                                    });
                                }}
                              />
                              <DaterangePicker
                                isClearable={true}
                                labelStartDate="Expire at"
                                startDate={field?.value?.end ?? null}
                                endDate={null}
                                selectsRange={false}
                                alwaysAsArray={true}
                                inline={true}
                                disabled={!isFullyEditable}
                                onChange={(range) => {
                                  range[0] &&
                                    setValue(StoryFields.AvilableDateRange, {
                                      start: field?.value?.start,
                                      end: range[0],
                                    });
                                }}
                              />
                            </FlexContainer>

                            {
                              // eslint-disable-next-line
                              //@ts-ignore
                              errors?.[StoryFields.AvilableDateRange]?.message && (
                                <Text color={theme.colors.red} size={11 / 16} weight="bold">
                                  {/* eslint-disable-next-line */}
                                  {/* @ts-ignore */}
                                  {errors?.[StoryFields.AvilableDateRange]?.message}
                                </Text>
                              )
                            }
                          </FlexContainer>
                        )}
                      />

                      <Input
                        label="Story reward"
                        required
                        control={control}
                        type="number"
                        width="100%"
                        outerWidth="100%"
                        name={StoryFields.StoryReward}
                        error={errors[StoryFields.StoryReward]}
                        minWidth="200px"
                        placeholder="Story reward"
                        disabled={!isFullyEditable}
                        defaultValue={0}
                      />
                    </FlexContainer>
                  </FlexContainer>

                  <FlexContainer margin={{ marginTop: '20px' }}>
                    <SelectForm
                      required
                      placeholder="Publisher"
                      name={StoryFields.Publisher}
                      control={control}
                      disabled={true}
                      values={creatorList}
                      error={errors?.[StoryFields.Publisher]}
                      defaultValue={gotivaCreator}
                      width="400px"
                      style={{ position: 'relative' }}
                    />
                  </FlexContainer>
                </FlexContainer>

                <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="10px">
                  <Text weight="bold" size={1.5}>
                    Story about
                  </Text>
                  <Controller
                    name={StoryFields.StoryAbout}
                    control={control}
                    render={({ field }) => (
                      <TextEditor
                        value={field.value}
                        disabled={false}
                        setValue={field.onChange}
                        placeholder="Start typing..."
                      />
                    )}
                  />
                  {errors?.[StoryFields.StoryAbout]?.message && (
                    <Text color={theme.colors.red} size={11 / 16} weight="bold">
                      {errors?.[StoryFields.StoryAbout]?.message}
                    </Text>
                  )}
                </FlexContainer>

                <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="10px">
                  <Text weight="bold" size={1.5}>
                    Story Chapters
                  </Text>
                  <FlexContainer
                    flexDirection="column"
                    dimensions={{ width: '100%' }}
                    gap="10px"
                    border={{
                      border: `dashed 2px ${chapters?.length === 0 ? theme.colors.red : '#A3A8AE'}`,
                    }}
                    padding={{ padding: '8px' }}
                  >
                    {chapters?.length === 0 && (
                      <Text color="gray" textAlign="center" padding="10px 0">
                        Story must contain chapters. Make sure to create them.
                      </Text>
                    )}
                    {chapters.map((chapter, index) => (
                      <StoryChapterForm
                        key={chapter[StoryChapterFields.RenderKey]}
                        index={index}
                        onRemove={removeChapter}
                        disabled={!isFullyEditable}
                        update={updateChapter}
                        swapPosition={swap}
                        setFile={(key: string, file: File) => (filesRef.current[key] = file)}
                        chapterId={chapter[StoryChapterFields.Id]}
                        shouldHideRemoveButton={LIMITED_EDITING_STATUSES.includes(
                          existingStoryData?.data()?.status as ObjectValues<typeof STORY_STATUSES>,
                        )}
                      />
                    ))}

                    {isFullyEditable && (
                      <FlexContainer
                        flexDirection="row"
                        justifyContent="flex-end"
                        gap="10px"
                        alignItems="center"
                      >
                        <Button
                          style={{ minWidth: 300 }}
                          variant="primary"
                          height={40}
                          disabled={false}
                          onClick={() => addChapter('static')}
                        >
                          ADD STATIC CHAPTER
                        </Button>
                        <Button
                          style={{ minWidth: 300 }}
                          variant="primary"
                          height={40}
                          disabled={false}
                          onClick={() => addChapter('video')}
                        >
                          ADD VIDEO CHAPTER
                        </Button>
                      </FlexContainer>
                    )}
                  </FlexContainer>
                </FlexContainer>

                <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="10px">
                  <Text weight="bold" size={1.5}>
                    Story end
                  </Text>
                  <Controller
                    name={StoryFields.StoryEnd}
                    control={control}
                    render={({ field }) => (
                      <TextEditor
                        value={field.value}
                        disabled={false}
                        setValue={field.onChange}
                        placeholder="Start typing..."
                      />
                    )}
                  />
                  {errors?.[StoryFields.StoryEnd]?.message && (
                    <Text color={theme.colors.red} size={11 / 16} weight="bold">
                      {errors?.[StoryFields.StoryEnd]?.message}
                    </Text>
                  )}
                </FlexContainer>

                <FlexContainer
                  dimensions={{ width: '100%', minHeight: '256px' }}
                  flexDirection="column"
                  gap="5px"
                >
                  <Text weight="bold" size={1.5}>
                    Soundtrack
                  </Text>
                  <FileUpload
                    initialState={
                      existingStoryData?.data()?.soundtrackUrl
                        ? {
                            state: 'exists',
                            url: existingStoryData?.data()?.soundtrackUrl || undefined,
                          }
                        : undefined
                    }
                    onFileAddedHook={(fileState, file) => {
                      setValue(StoryFields.SoundtrackUrl, fileState);
                      if (file) {
                        filesRef.current.soundtrack = file;
                      }
                    }}
                    hasFileRender={(fileState, removeFile) => (
                      <HasAudio fileState={fileState} removeFile={removeFile} />
                    )}
                    noFileRender={() => <NoFile fileTypes={['mp3']} />}
                    fileUploadProps={{
                      styling: {
                        dimensions: { flex: '1', width: '100%', minHeight: '300px' },
                        gap: '10px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { padding: '16px' },
                      },
                      accept: 'audio/mpeg',
                    }}
                  />
                </FlexContainer>
              </FlexContainer>

              <FlexContainer
                dimensions={{ width: '100%' }}
                gap="20px"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Button
                  style={{ minWidth: 200 }}
                  variant="danger"
                  disabled={isSubmitting || !isReady}
                  height={40}
                  onClick={() => {}}
                >
                  Cancel
                </Button>
                <Button
                  style={{ minWidth: 200 }}
                  variant="primary"
                  disabled={isSubmitting || !isReady}
                  height={40}
                  onClick={submit(false)}
                >
                  Save Draft
                </Button>
                <Button
                  style={{ minWidth: 200 }}
                  variant="primary"
                  disabled={isSubmitting || !isReady}
                  height={40}
                  onClick={submit(true)}
                >
                  Publish
                </Button>
              </FlexContainer>
            </FormProvider>
          )}
        </FlexContainer>
      </Box>
    </FlexContainer>
  );
};
